'use client';

import { usdToTrons } from '@shared/all/domains/products';
import type { ChangeEvent } from 'react';
import { useState } from 'react';

import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';

const BEST = bestKey('spend');

/** One hundred billion trons. The cheapest item divides it exactly, so
 * finishing on a balance of zero is genuinely possible. */
// $333,333 of AI — the joke only works if the number stays absurd.
const TREASURY = usdToTrons(333_333);

type Item = { name: string; emoji: string; price: number };

const CATALOGUE: Item[] = [
  { name: 'Cup of coffee', emoji: '☕', price: 5 },
  { name: 'Domain name', emoji: '🌐', price: 15 },
  { name: 'Primary source', emoji: '🔗', price: 40 },
  { name: 'One fact check', emoji: '✅', price: 120 },
  { name: 'Verified badge', emoji: '🔵', price: 800 },
  { name: 'Retraction notice', emoji: '✏️', price: 2_500 },
  { name: 'Freelance journalist', emoji: '🕵️', price: 9_000 },
  { name: 'Focus group', emoji: '👥', price: 25_000 },
  { name: 'Bot farm', emoji: '🤖', price: 60_000 },
  { name: 'Bus stop billboard', emoji: '🪧', price: 140_000 },
  { name: 'Podcast studio', emoji: '🎙️', price: 500_000 },
  { name: 'Small town newspaper', emoji: '📰', price: 1_200_000 },
  { name: 'Superbowl advert', emoji: '📺', price: 8_000_000 },
  { name: 'Research grant', emoji: '🔬', price: 15_000_000 },
  { name: 'Superyacht', emoji: '🛥️', price: 40_000_000 },
  { name: 'Private island', emoji: '🏝️', price: 90_000_000 },
  { name: 'Ghost town', emoji: '🏚️', price: 250_000_000 },
  { name: 'National archive', emoji: '🗄️', price: 600_000_000 },
  { name: 'Football club', emoji: '⚽', price: 1_500_000_000 },
  { name: 'Skyscraper', emoji: '🏙️', price: 3_000_000_000 },
  { name: 'Social network', emoji: '📱', price: 8_000_000_000 },
  { name: 'Data centre', emoji: '🗃️', price: 12_000_000_000 },
  { name: 'Space programme', emoji: '🚀', price: 25_000_000_000 },
  { name: 'The truth', emoji: '💎', price: 50_000_000_000 }
];

const trons = new Intl.NumberFormat('en-US');

const SpendTreasury = () => {
  const [owned, setOwned] = useState<Record<string, number>>({});
  const [showReceipt, setShowReceipt] = useState(false);

  const best = useLocalBest(BEST);
  const { record } = useGameTracking();

  const spent = CATALOGUE.reduce(
    (total, item) => total + (owned[item.name] ?? 0) * item.price,
    0
  );
  const balance = TREASURY - spent;

  /** Set an exact quantity, clamped to what the remaining balance allows. */
  const setQuantity = (item: Item, requested: number) => {
    const current = owned[item.name] ?? 0;
    const safe = Number.isFinite(requested)
      ? Math.max(0, Math.floor(requested))
      : 0;
    const affordable = current + Math.floor(balance / item.price);
    const next = Math.min(safe, affordable);

    if (next === current) return;

    const updated = { ...owned, [item.name]: next };
    setOwned(updated);

    const remaining =
      TREASURY -
      CATALOGUE.reduce(
        (total, entry) => total + (updated[entry.name] ?? 0) * entry.price,
        0
      );
    record('spend', remaining, {
      itemsOwned: Object.values(updated).filter((n) => n > 0).length
    });
  };

  const typeQuantity = (item: Item) => (event: ChangeEvent<HTMLInputElement>) =>
    setQuantity(
      item,
      event.target.value === '' ? 0 : Number(event.target.value)
    );

  const reset = () => {
    setOwned({});
    setShowReceipt(false);
  };

  const receipt = CATALOGUE.filter((item) => (owned[item.name] ?? 0) > 0);

  return (
    <div>
      {/* Balance follows you down the catalogue — it is the whole game. */}
      <div className="sticky top-2 z-sticky mb-6 rounded-2xl border border-ds-border-standard bg-background-secondary/95 p-4 backdrop-blur">
        <p className="text-xs uppercase tracking-wide text-textGray">
          Your balance
        </p>
        <p
          className={`text-3xl font-bold tabular-nums sm:text-4xl ${
            balance === 0 ? 'text-green-500' : 'text-textPrimary'
          }`}
        >
          {trons.format(balance)}
          <span className="ml-2 text-base font-normal text-textSecondary">
            trons
          </span>
        </p>

        {balance === 0 && (
          <p className="mt-1 text-sm text-green-500">
            Every last tron. The community pool thanks you.
          </p>
        )}

        <div className="mt-3 flex gap-4 text-sm">
          <button
            type="button"
            onClick={() => setShowReceipt(!showReceipt)}
            className="text-textSecondary underline underline-offset-2 transition-colors hover:text-textPrimary"
          >
            {showReceipt ? 'Back to shop' : `Receipt (${receipt.length})`}
          </button>
          <button
            type="button"
            onClick={reset}
            className="text-textGray underline underline-offset-2 transition-colors hover:text-textPrimary"
          >
            Start over
          </button>
        </div>
      </div>

      {showReceipt ? (
        <div className="rounded-2xl border border-ds-border-standard bg-background-secondary p-5">
          <h2 className="mb-4 font-emphasis text-textPrimary">Receipt</h2>

          {receipt.length === 0 ? (
            <p className="text-sm text-textGray">
              You have not bought anything yet.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {receipt.map((item) => (
                <li
                  key={item.name}
                  className="flex justify-between gap-4 text-sm"
                >
                  <span className="text-textSecondary">
                    <span aria-hidden="true">{item.emoji}</span> {item.name}{' '}
                    <span className="text-textGray">
                      × {trons.format(owned[item.name] ?? 0)}
                    </span>
                  </span>
                  <span className="tabular-nums text-textPrimary">
                    {trons.format((owned[item.name] ?? 0) * item.price)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex justify-between border-t border-ds-border-subtle pt-3 font-emphasis text-textPrimary">
            <span>Total spent</span>
            <span className="tabular-nums">{trons.format(spent)}</span>
          </div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {CATALOGUE.map((item) => {
            const quantity = owned[item.name] ?? 0;
            const canBuy = balance >= item.price;

            return (
              <li
                key={item.name}
                className="flex flex-col rounded-2xl border border-ds-border-standard bg-background-secondary p-4"
              >
                <span aria-hidden="true" className="text-3xl">
                  {item.emoji}
                </span>
                <span className="mt-2 font-emphasis text-textPrimary">
                  {item.name}
                </span>
                <span className="mb-3 text-sm tabular-nums text-textSecondary">
                  {trons.format(item.price)} trons
                </span>

                <div className="mt-auto flex items-center gap-2">
                  <button
                    type="button"
                    disabled={quantity === 0}
                    onClick={() => setQuantity(item, quantity - 1)}
                    className="rounded-full border border-ds-border-standard px-4 py-1.5 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Sell
                  </button>

                  <input
                    type="number"
                    min={0}
                    value={quantity}
                    onChange={typeQuantity(item)}
                    aria-label={`Quantity of ${item.name}`}
                    className="w-full min-w-0 rounded-lg border border-ds-border-standard bg-background-primary px-2 py-1.5 text-center tabular-nums text-textPrimary outline-none focus:border-ds-border-strong"
                  />

                  <button
                    type="button"
                    disabled={!canBuy}
                    onClick={() => setQuantity(item, quantity + 1)}
                    className="rounded-full bg-primary px-4 py-1.5 text-sm font-emphasis text-white transition-colors hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    Buy
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {best !== null && (
        <p className="mt-6 text-sm text-textGray">
          Closest you have got to zero: {trons.format(Number(best))} trons left
        </p>
      )}
    </div>
  );
};

export default SpendTreasury;
